import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import { FiArrowDown, FiArrowUp, FiEye, FiFileText } from "react-icons/fi";
import ClientInvoiceStatusBadge from "./ClientInvoiceStatusBadge";

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function money(value, currency) {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount / 100);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(amount / 100);
  }
}

export default function ClientInvoiceTable({
  invoices,
  loading,
  page,
  pageSize,
  currency,
  onPageChange,
  onView,
}) {
  const pages = Math.max(1, Math.ceil(invoices.length / pageSize));
  const rows = invoices.slice((page - 1) * pageSize, page * pageSize);

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-violet-100 border-t-violet-600" />
        <p className="mt-4 text-sm font-medium text-slate-600">
          Loading invoices…
        </p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-left">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-5 py-4">Invoice #</th>
              <th className="px-5 py-4">Customer</th>
              <th className="px-5 py-4">Issue Date</th>
              <th className="px-5 py-4">Due Date</th>
              <th className="px-5 py-4 text-right">Total</th>
              <th className="px-5 py-4 text-right">Balance Due</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {rows.map((invoice) => (
              <tr
                key={invoice.id}
                onClick={() => onView(invoice)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onView(invoice);
                  }
                }}
                tabIndex={0}
                className="cursor-pointer text-sm outline-none transition hover:bg-slate-50 focus:bg-violet-50/50"
              >
                <td className="whitespace-nowrap px-5 py-4 font-bold text-slate-900">
                  {invoice.invoice_number || "—"}
                </td>
                <td className="max-w-[230px] px-5 py-4">
                  <p className="truncate font-semibold text-slate-800">
                    {invoice.customer?.name || "Customer unavailable"}
                  </p>
                  {invoice.customer?.company_name && (
                    <p className="mt-1 truncate text-xs text-slate-400">
                      {invoice.customer.company_name}
                    </p>
                  )}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                  {dateLabel(invoice.issue_date)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                  {dateLabel(invoice.due_date)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-right font-medium tabular-nums text-slate-700">
                  {money(invoice.total_amount, invoice.currency || currency)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-right font-semibold tabular-nums text-slate-900">
                  {money(invoice.amount_due, invoice.currency || currency)}
                </td>
                <td className="px-5 py-4">
                  <ClientInvoiceStatusBadge status={invoice.displayStatus} />
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onView(invoice);
                    }}
                    aria-label={`View invoice ${invoice.invoice_number || ""}`}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold text-violet-700 transition hover:bg-violet-50"
                  >
                    <SafeIcon icon={FiEye} />
                    View
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-14 text-center">
                  <SafeIcon icon={FiFileText} className="mx-auto text-3xl text-slate-300" />
                  <p className="mt-3 text-sm font-bold text-slate-800">
                    No invoices match these filters
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Try another search or date range.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          {invoices.length
            ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(
                page * pageSize,
                invoices.length,
              )} of ${invoices.length}`
            : "0 invoices"}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="inline-flex items-center gap-2 px-2 text-xs text-slate-500">
            <SafeIcon icon={page % 2 ? FiArrowDown : FiArrowUp} />
            Page {page} of {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </footer>
    </section>
  );
}